"use client";

import { c as _c } from "react/compiler-runtime";
import OverlappingAvatars from "@/components/common/OverlappingAvatars";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { parseIpfsUrl } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { GroupedReactionsItem, INotificationDropReacted } from "@/types/feed.types";
import { Fragment, useEffect, useRef } from "react";
import NotificationsFollowAllBtn from "../NotificationsFollowAllBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
import ReactionEmojiPreview from "./ReactionEmojiPreview";
function notificationsLatestPerUser(notifications: GroupedReactionsItem["notifications"]): INotificationDropReacted[] {
  const byUser = new Map<string, INotificationDropReacted>();
  for (const n of notifications) {
    const key = n.related_identity?.id ?? n.related_identity?.handle ?? "";
    if (!key) continue;
    const existing = byUser.get(key);
    if (!existing || n.id > existing.id) {
      byUser.set(key, n);
    }
  }
  const list = Array.from(byUser.values());
  list.sort((a, b) => a.id - b.id);
  return list;
}
type ReactionGroup = {
  readonly reaction: string;
  readonly rawId: string;
  readonly notifications: INotificationDropReacted[];
};
function groupLatestByReaction(latestPerUser: INotificationDropReacted[]): ReactionGroup[] {
  const byReaction = new Map<string, INotificationDropReacted[]>();
  for (const n of latestPerUser) {
    const reaction = n.additional_context.reaction;
    const list = byReaction.get(reaction) ?? [];
    list.push(n);
    byReaction.set(reaction, list);
  }
  return Array.from(byReaction.entries()).map(([reaction, list]) => ({
    reaction,
    rawId: reaction.replaceAll(":", ""),
    notifications: list
  })).sort((a, b) => {
    const aLatest = Math.max(...a.notifications.map(n => n.created_at));
    const bLatest = Math.max(...b.notifications.map(n => n.created_at));
    return bLatest - aLatest;
  });
}
interface NotificationDropReactedGroupProps {
  readonly group: GroupedReactionsItem;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly onMarkAsRead?: ((notificationIds: number[]) => void) | undefined;
}
export default function NotificationDropReactedGroup(t0) {
  const $ = _c(33);
  const {
    group,
    activeDrop,
    onReply,
    onDropContentClick,
    onMarkAsRead
  } = t0;
  const rootRef = useRef(null);
  const hasMarkedRef = useRef(false);
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    drop,
    notifications,
    createdAt
  } = group;
  const isDirectMessage = getIsDirectMessage(drop.wave);
  let ids;
  let t1;
  if ($[0] !== notifications) {
    ids = notifications.map(_temp);
    t1 = ids.join(",");
    $[0] = notifications;
    $[1] = ids;
    $[2] = t1;
  } else {
    ids = $[1];
    t1 = $[2];
  }
  const idsKey = t1;
  const latestPerUser = notificationsLatestPerUser(notifications);
  const fullReactors = latestPerUser.map(_temp2);
  const reactionGroups = groupLatestByReaction(latestPerUser);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      hasMarkedRef.current = false;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== idsKey) {
    t3 = [idsKey];
    $[4] = idsKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== ids || $[7] !== onDropContentClick || $[8] !== onMarkAsRead) {
    t4 = clickedDrop => {
      if (!hasMarkedRef.current && onMarkAsRead) {
        hasMarkedRef.current = true;
        onMarkAsRead(ids);
      }
      onDropContentClick?.(clickedDrop);
    };
    $[6] = ids;
    $[7] = onDropContentClick;
    $[8] = onMarkAsRead;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleDropContentClick = t4;
  const t5 = "tw-flex tw-w-full tw-flex-col tw-gap-y-2";
  const t6 = "tw-flex tw-items-center tw-gap-x-2";
  const t7 = "tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-gap-y-2 min-[390px]:tw-flex-row min-[390px]:tw-items-center min-[390px]:tw-justify-between min-[390px]:tw-gap-x-2";
  const t8 = "tw-flex tw-flex-wrap tw-items-center tw-gap-x-1";
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-mr-1 tw-text-sm tw-font-normal tw-text-iron-400">New reactions</span>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = reactionGroups.map(_temp5);
  let t11;
  if ($[11] !== createdAt) {
    t11 = <NotificationTimestamp createdAt={createdAt} />;
    $[11] = createdAt;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t11 || $[15] !== t9) {
    t12 = <div className={t8}>{t9}{t10}{t11}</div>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t9;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== fullReactors) {
    t13 = fullReactors.length > 0 && <div className="tw-flex-shrink-0"><NotificationsFollowAllBtn profiles={fullReactors} size={UserFollowBtnSize.SMALL} /></div>;
    $[17] = fullReactors;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div className={t6}><div className={t7}>{t12}{t13}</div></div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const T0 = NotificationDrop;
  const t15 = createReplyClickHandler(drop.wave.id, isDirectMessage);
  const t16 = createQuoteClickHandler(isDirectMessage);
  let t17;
  if ($[22] !== T0 || $[23] !== activeDrop || $[24] !== drop || $[25] !== handleDropContentClick || $[26] !== onReply || $[27] !== t15 || $[28] !== t16) {
    t17 = <T0 drop={drop} activeDrop={activeDrop} onReply={onReply} onReplyClick={t15} onQuoteClick={t16} onDropContentClick={handleDropContentClick} />;
    $[22] = T0;
    $[23] = activeDrop;
    $[24] = drop;
    $[25] = handleDropContentClick;
    $[26] = onReply;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t14 || $[31] !== t17) {
    t18 = <div ref={rootRef} className={t5}>{t14}{t17}</div>;
    $[30] = t14;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  return t18;
}
function _temp5(rg, index) {
  const profiles = rg.notifications.map(_temp3);
  const avatarItems = profiles.map(_temp4);
  return <Fragment key={rg.reaction}>{index > 0 && <span className="tw-mx-0.5 tw-flex-shrink-0 tw-text-xs tw-font-bold tw-text-iron-400">•</span>}<div className="tw-flex tw-h-7 tw-flex-shrink-0 tw-items-center tw-gap-x-0.5 tw-overflow-visible"><OverlappingAvatars items={avatarItems} size="md" overlapClass="-tw-space-x-1" /><div className="tw-relative tw-flex-shrink-0 tw-overflow-visible tw-transition-transform tw-duration-200 tw-ease-out hover:!tw-z-[100] hover:tw-scale-110"><ReactionEmojiPreview rawId={rg.rawId} /></div></div></Fragment>;
}
function _temp4(profile) {
  const key = profile.id ?? profile.handle ?? profile.primary_address ?? "";
  const href = profile.handle ? `/${profile.handle}` : undefined;
  const displayName = profile.handle ?? (profile.id === null || profile.id === undefined ? undefined : String(profile.id));
  const title = displayName !== undefined && displayName !== "" ? displayName : undefined;
  return {
    key,
    pfpUrl: profile.pfp ? parseIpfsUrl(profile.pfp) : null,
    ariaLabel: profile.handle ? `View @${profile.handle}` : "View profile",
    fallback: profile.handle?.slice(0, 2).toUpperCase() ?? "?",
    ...(href !== undefined && {
      href
    }),
    ...(title !== undefined && {
      title
    })
  };
}
function _temp3(n_1) {
  return n_1.related_identity;
}
function _temp2(n_0) {
  return n_0.related_identity;
}
function _temp(n) {
  return n.id;
}